import React, {useMemo, useState} from 'react';

import {
  ChatAssistantReportMessageContents,
  SaveChatAssistantReplyOverflowButton,
  SaveChatAssistantReportButton,
} from 'llm/chat-library/messages/ChatAssistantMessageMenu';
import {ProgressData} from 'llm/chat-library/progress/ProgressTypes';
import LLMFeedbackChatToolbar from 'llm/feedback/LLMFeedbackChatToolbar';
import LLMLoadingMessage from 'llm/loading/LLMLoadingMessage';
import LLMMarkdownSection from 'llm/markdown/LLMMarkdownSection';
import {assertNever} from 'util/Types';

import {ChatMessageBubble, ChatMessageToolbar} from './ChatMessageBubble';
import {
  ClientStreamingAssistantMessage,
  DisplayMessageChatEventNode,
  DisplayMessageAssistantResponseType,
} from './MessageTypes';
import {getDisplayMessageAssistantResponseType} from './MessageUtils';

type AssistantMessage = DisplayMessageChatEventNode | ClientStreamingAssistantMessage;

export interface ChatAssistantMessageBubbleProps {
  event: AssistantMessage;
  progressData?: ProgressData;
  /** If true, hides interactive components with side-effects. */
  readonly?: boolean;
  chatSessionId: string;
}

function getToolbarActions(
  event: AssistantMessage,
  {
    readonly,
    chatSessionId,
    markdown,
    onReplyOverflowOpenChange,
  }: {
    readonly: boolean;
    chatSessionId: string;
    markdown: string;
    onReplyOverflowOpenChange: (isOpen: boolean) => void;
  }
): React.ReactNode {
  const responseType = getDisplayMessageAssistantResponseType(event);
  switch (responseType) {
    case DisplayMessageAssistantResponseType.REPORT: {
      const messageText = getMarkdown(event);
      return !readonly ? (
        <SaveChatAssistantReportButton markdownReport={messageText} chatSessionId={chatSessionId} />
      ) : null;
    }
    case DisplayMessageAssistantResponseType.REPLY:
      return !readonly ? (
        <SaveChatAssistantReplyOverflowButton
          markdownReport={markdown}
          chatSessionId={chatSessionId}
          onOpenChange={onReplyOverflowOpenChange}
        />
      ) : null;
    default:
      assertNever(responseType);
      return null;
  }
}
function getMarkdown(event: AssistantMessage): string {
  return event.messageBlocks
    .flatMap((block) => {
      switch (block.__typename) {
        case 'DisplayTextBlockNode':
          return block.text;
        case 'DisplayFileBlockNode':
          // Ignore file blocks for now. Deep Research returns file blocks when the image generation tool is called.
          // These are rendered inline using bnch_file tags, so we shouldn't render them here.
          return [];
        default:
          assertNever(block);
          return [];
      }
    })
    .join('\n');
}

export default function ChatAssistantMessageBubble({
  event,
  progressData,
  readonly = false,
  chatSessionId,
}: ChatAssistantMessageBubbleProps): JSX.Element {
  const [isReplyOverflowOpen, setIsReplyOverflowOpen] = useState(false);

  // Implicitly, message blocks are markdown. In the future, that might not be the case, and the block
  // itself will indicate its type.
  const markdown = useMemo(() => getMarkdown(event), [event]);
  const responseType = getDisplayMessageAssistantResponseType(event);

  const toolbar = useMemo(
    () => (
      <ChatMessageToolbar
        left={
          // Only render the feedback toolbar for persisted messages.
          !readonly && event.__typename === 'DisplayMessageChatEventNode' ? (
            <LLMFeedbackChatToolbar
              chatEventId={event.id}
              chatEventCreatedAt={event.createdAt}
              agentType={event.agentType}
            />
          ) : (
            <div>
              {event.__typename === 'ClientStreamingAssistantMessage' && event.progressMessage ? (
                <LLMLoadingMessage text={event.progressMessage} />
              ) : null}
            </div>
          )
        }
        right={getToolbarActions(event, {
          readonly,
          chatSessionId,
          markdown,
          onReplyOverflowOpenChange: setIsReplyOverflowOpen,
        })}
        hideRightUntilHover={responseType === DisplayMessageAssistantResponseType.REPLY}
        showRightAction={responseType !== DisplayMessageAssistantResponseType.REPLY || isReplyOverflowOpen}
      />
    ),
    [event, readonly, chatSessionId, markdown, responseType, isReplyOverflowOpen]
  );

  switch (responseType) {
    case DisplayMessageAssistantResponseType.REPLY:
      return (
        <ChatMessageBubble variant="reply" toolbar={toolbar}>
          <LLMMarkdownSection markdown={markdown} chatSessionId={chatSessionId} />
        </ChatMessageBubble>
      );
    case DisplayMessageAssistantResponseType.REPORT:
      return (
        <ChatMessageBubble variant="report" toolbar={toolbar}>
          <ChatAssistantReportMessageContents
            report={markdown}
            progressData={progressData}
            readonly={readonly}
            chatSessionId={chatSessionId}
          />
        </ChatMessageBubble>
      );
    default:
      assertNever(responseType);
      // NB: This should never be exercised as responseType is generated on the client.
      // If we move this to be a persisted field, this fallback may be exercised.
      return (
        <ChatMessageBubble variant="reply" toolbar={toolbar}>
          <LLMMarkdownSection markdown={markdown} chatSessionId={chatSessionId} />
        </ChatMessageBubble>
      );
  }
}
